import { useCallback, useEffect, useState } from 'react';

/**
 * The shape of `beforeinstallprompt` in Chromium-based browsers. We can't use
 * lib.dom types directly because not every TypeScript build ships the augmented
 * `BeforeInstallPromptEvent` global. Keep the surface we actually consume.
 */
export interface BeforeInstallPromptEvent extends Event {
  readonly userChoice: Promise<{ outcome: 'accepted' | 'dismissed'; platform: string }>;
  prompt(): Promise<void>;
}

export interface InstallPromptApi {
  /** True when a `beforeinstallprompt` is captured and the app is not yet installed. */
  readonly isInstallable: boolean;
  /** True when running in standalone mode (PWA already installed) or after a successful install. */
  readonly isInstalled: boolean;
  /** Triggers the browser install dialog. No-op when not installable. */
  install(): Promise<void>;
}

/**
 * Subscribes to `beforeinstallprompt` (and `appinstalled`) and exposes a
 * deferred-install API. The Chromium install prompt fires once per page load;
 * we hold the event, defer the default banner, and replay it when the user
 * clicks our CTA. SSR-safe: starts with `isInstallable: false` and upgrades
 * after mount.
 *
 * Caveats:
 * - Safari (desktop + iOS < 16.4) never fires `beforeinstallprompt` — the
 *   button stays hidden, which is correct (those installs go through Share →
 *   Add to Home Screen).
 * - Inside the Capacitor WebView the event also doesn't fire, so the CTA
 *   never shows in a `S4K_TARGET=native` build.
 */
export function useInstallPrompt(): InstallPromptApi {
  const [promptEvent, setPromptEvent] = useState<BeforeInstallPromptEvent | null>(null);
  const [isInstalled, setIsInstalled] = useState(false);

  useEffect(() => {
    if (typeof window === 'undefined') return;

    // An app running in standalone display-mode is, by definition, already
    // installed — no CTA needed.
    if (window.matchMedia?.('(display-mode: standalone)')?.matches) {
      setIsInstalled(true);
      return;
    }

    const onBeforeInstallPrompt = (e: Event) => {
      // Stop the browser's auto-banner so we control the prompt ourselves.
      e.preventDefault();
      setPromptEvent(e as BeforeInstallPromptEvent);
    };
    const onAppInstalled = () => {
      setPromptEvent(null);
      setIsInstalled(true);
    };
    window.addEventListener('beforeinstallprompt', onBeforeInstallPrompt);
    window.addEventListener('appinstalled', onAppInstalled);
    return () => {
      window.removeEventListener('beforeinstallprompt', onBeforeInstallPrompt);
      window.removeEventListener('appinstalled', onAppInstalled);
    };
  }, []);

  const install = useCallback(async () => {
    if (!promptEvent) return;
    await promptEvent.prompt();
    const choice = await promptEvent.userChoice;
    // Either outcome clears the event — Chrome won't reuse it, and a stale
    // prompt would let the CTA appear a second time.
    setPromptEvent(null);
    if (choice.outcome === 'accepted') setIsInstalled(true);
  }, [promptEvent]);

  return {
    isInstallable: promptEvent !== null && !isInstalled,
    isInstalled,
    install,
  };
}